import tippy from "tippy.js";
import { addClass, removeClass } from "../utils/dom-helpers.js";
import { showUserFriendlyNotyf, __ } from "../utils/notyf-helpers.js";

/**
 * @file Consolidated UI Components Module
 * @description Reusable UI components and utilities
 */

/**
 * Loading indicator component
 */
export class LoadingIndicator {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      message: __("Loading...", "iphub"),
      showSpinner: true,
      ...options,
    };
    this.element = null;
  }

  show() {
    this.element = document.createElement("div");
    this.element.className = "loading-indicator";

    let html = "";
    if (this.options.showSpinner) {
      html += '<div class="spinner"></div>';
    }
    html += `<p>${this.options.message}</p>`;

    this.element.innerHTML = html;

    if (this.container) {
      this.container.appendChild(this.element);
    }

    return this.element;
  }

  hide() {
    if (this.element && this.element.parentNode) {
      this.element.remove();
    }
    this.element = null;
  }

  updateMessage(message) {
    if (this.element) {
      const messageElement = this.element.querySelector("p");
      if (messageElement) {
        messageElement.textContent = message;
      }
    }
  }
}

/**
 * Error message component
 */
export class ErrorMessage {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      message: __("An error occurred", "iphub"),
      showRetry: true,
      retryText: __("Retry", "iphub"),
      ...options,
    };
    this.element = null;
    this.onRetry = null;
  }

  show(message = null, onRetry = null) {
    this.element = document.createElement("div");
    this.element.className = "error-message";

    const displayMessage = message || this.options.message;

    let html = `
      <div class="error-icon">⚠️</div>
      <p>${displayMessage}</p>
    `;

    if (this.options.showRetry) {
      html += `<button class="retry-btn">${this.options.retryText}</button>`;
    }

    this.element.innerHTML = html;

    if (this.container) {
      this.container.appendChild(this.element);
    }

    // Add retry functionality
    if (this.options.showRetry && onRetry) {
      this.onRetry = onRetry;
      const retryBtn = this.element.querySelector(".retry-btn");
      if (retryBtn) {
        retryBtn.addEventListener("click", () => {
          this.hide();
          onRetry();
        });
      }
    }

    return this.element;
  }

  hide() {
    if (this.element && this.element.parentNode) {
      this.element.remove();
    }
    this.element = null;
    this.onRetry = null;
  }
}

/**
 * Modal component
 */
export class Modal {
  constructor(options = {}) {
    this.options = {
      title: "",
      content: "",
      showClose: true,
      closeOnOverlay: true,
      ...options,
    };
    this.element = null;
    this.isOpen = false;
  }

  create() {
    this.element = document.createElement("div");
    this.element.className = "modal-overlay";

    const modalContent = document.createElement("div");
    modalContent.className = "modal-content";

    let html = "";

    if (this.options.title) {
      html += `<div class="modal-header">
        <h3>${this.options.title}</h3>
        ${this.options.showClose ? '<button class="modal-close">&times;</button>' : ""}
      </div>`;
    }

    html += `<div class="modal-body">${this.options.content}</div>`;

    modalContent.innerHTML = html;
    this.element.appendChild(modalContent);

    // Add event listeners
    if (this.options.showClose) {
      const closeBtn = this.element.querySelector(".modal-close");
      if (closeBtn) {
        closeBtn.addEventListener("click", () => this.close());
      }
    }

    if (this.options.closeOnOverlay) {
      this.element.addEventListener("click", (e) => {
        if (e.target === this.element) {
          this.close();
        }
      });
    }

    return this.element;
  }

  open() {
    if (!this.element) {
      this.create();
    }

    document.body.appendChild(this.element);
    this.isOpen = true;

    // Add open class for animations
    setTimeout(() => {
      this.element.classList.add("modal-open");
    }, 10);

    return this;
  }

  close() {
    if (!this.element || !this.isOpen) return;

    this.element.classList.remove("modal-open");

    setTimeout(() => {
      if (this.element && this.element.parentNode) {
        this.element.remove();
      }
      this.isOpen = false;
    }, 300);

    return this;
  }

  setContent(content) {
    if (this.element) {
      const body = this.element.querySelector(".modal-body");
      if (body) {
        body.innerHTML = content;
      }
    }
    return this;
  }

  setTitle(title) {
    if (this.element) {
      const header = this.element.querySelector(".modal-header h3");
      if (header) {
        header.textContent = title;
      }
    }
    return this;
  }
}

/**
 * Tooltip component
 */
export class Tooltip {
  constructor(options = {}) {
    this.options = {
      content: "",
      position: "top",
      delay: 200,
      ...options,
    };
    this.element = null;
    this.timeout = null;
  }

  show(target, content = null) {
    this.hide();

    this.element = document.createElement("div");
    this.element.className = `tooltip tooltip-${this.options.position}`;
    this.element.textContent = content || this.options.content;

    document.body.appendChild(this.element);

    // Position tooltip
    this.positionTooltip(target);

    return this.element;
  }

  hide() {
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }

    if (this.element && this.element.parentNode) {
      this.element.remove();
    }
    this.element = null;
  }

  positionTooltip(target) {
    if (!this.element || !target) return;

    const targetRect = target.getBoundingClientRect();
    const tooltipRect = this.element.getBoundingClientRect();

    let top, left;

    switch (this.options.position) {
      case "top":
        top = targetRect.top - tooltipRect.height - 5;
        left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
        break;
      case "bottom":
        top = targetRect.bottom + 5;
        left = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2;
        break;
      case "left":
        top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
        left = targetRect.left - tooltipRect.width - 5;
        break;
      case "right":
        top = targetRect.top + targetRect.height / 2 - tooltipRect.height / 2;
        left = targetRect.right + 5;
        break;
    }

    this.element.style.top = `${top}px`;
    this.element.style.left = `${left}px`;
  }
}

/**
 * Notification component
 */
export class Notification {
  constructor(options = {}) {
    this.options = {
      duration: 5000,
      position: "top-right",
      ...options,
    };
    this.notifications = [];
  }

  show(message, type = "info", options = {}) {
    const notification = document.createElement("div");
    notification.className = `notification notification-${type} notification-${this.options.position}`;

    const icon = this.getIcon(type);
    notification.innerHTML = `
      <div class="notification-icon">${icon}</div>
      <div class="notification-content">
        <p>${message}</p>
      </div>
      <button class="notification-close">&times;</button>
    `;

    // Add to page
    this.addToContainer(notification);

    // Add to tracking array
    this.notifications.push(notification);

    // Auto remove
    if (this.options.duration > 0) {
      setTimeout(() => {
        this.remove(notification);
      }, this.options.duration);
    }

    // Close button
    const closeBtn = notification.querySelector(".notification-close");
    if (closeBtn) {
      closeBtn.addEventListener("click", () => {
        this.remove(notification);
      });
    }

    return notification;
  }

  remove(notification) {
    if (notification && notification.parentNode) {
      notification.classList.add("notification-hiding");
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
        this.notifications = this.notifications.filter((n) => n !== notification);
      }, 300);
    }
  }

  clearAll() {
    this.notifications.forEach((notification) => {
      this.remove(notification);
    });
  }

  getIcon(type) {
    const icons = {
      success: "✅",
      error: "❌",
      warning: "⚠️",
      info: "ℹ️",
    };
    return icons[type] || icons.info;
  }

  addToContainer(notification) {
    let container = document.querySelector(".notification-container");

    if (!container) {
      container = document.createElement("div");
      container.className = "notification-container";
      document.body.appendChild(container);
    }

    container.appendChild(notification);
  }
}

/**
 * Form validation component
 */
export class FormValidator {
  constructor(form, options = {}) {
    this.form = form;
    this.options = {
      validateOnInput: true,
      showErrors: true,
      ...options,
    };
    this.errors = new Map();
    this.setupValidation();
  }

  setupValidation() {
    if (this.options.validateOnInput) {
      this.form.addEventListener("input", (e) => {
        this.validateField(e.target);
      });
    }

    this.form.addEventListener("submit", (e) => {
      if (!this.validateForm()) {
        e.preventDefault();
      }
    });
  }

  validateField(field) {
    const fieldName = field.name;
    const value = field.value.trim();
    const rules = field.dataset.validation?.split("|") || [];

    let fieldErrors = [];

    rules.forEach((rule) => {
      const [ruleName, ruleValue] = rule.split(":");

      switch (ruleName) {
        case "required":
          if (!value) {
            fieldErrors.push(__("This field is required", "iphub"));
          }
          break;
        case "email":
          if (value && !this.isValidEmail(value)) {
            fieldErrors.push(__("Please enter a valid email address", "iphub"));
          }
          break;
        case "min":
          if (value && value.length < parseInt(ruleValue)) {
            fieldErrors.push(
              __("Minimum length is", "iphub") + ` ${ruleValue} ${__("characters", "iphub")}`,
            );
          }
          break;
        case "max":
          if (value && value.length > parseInt(ruleValue)) {
            fieldErrors.push(
              __("Maximum length is", "iphub") + ` ${ruleValue} ${__("characters", "iphub")}`,
            );
          }
          break;
      }
    });

    if (fieldErrors.length > 0) {
      this.errors.set(fieldName, fieldErrors);
      this.showFieldError(field, fieldErrors[0]);
    } else {
      this.errors.delete(fieldName);
      this.clearFieldError(field);
    }

    return fieldErrors.length === 0;
  }

  validateForm() {
    const fields = this.form.querySelectorAll("[data-validation]");
    let isValid = true;

    fields.forEach((field) => {
      if (!this.validateField(field)) {
        isValid = false;
      }
    });

    return isValid;
  }

  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  showFieldError(field, message) {
    if (!this.options.showErrors) return;

    this.clearFieldError(field);

    const errorElement = document.createElement("div");
    errorElement.className = "field-error";
    errorElement.textContent = message;

    field.parentNode.appendChild(errorElement);
    addClass(field, "field-error");
  }

  clearFieldError(field) {
    if (!this.options.showErrors) return;

    const errorElement = field.parentNode.querySelector(".field-error");
    if (errorElement) {
      errorElement.remove();
    }
    removeClass(field, "field-error");
  }

  getErrors() {
    return Object.fromEntries(this.errors);
  }

  clearErrors() {
    this.errors.clear();
    this.form.querySelectorAll(".field-error").forEach((error) => {
      error.remove();
    });
    this.form.querySelectorAll(".field-error").forEach((field) => {
      removeClass(field, "field-error");
    });
  }
}

/**
 * Initialize UI components
 */
export function initUIComponents() {
  // Create global instances
  window.IPHUB_LOADING_INDICATOR = new LoadingIndicator();
  window.IPHUB_ERROR_MESSAGE = new ErrorMessage();
  window.IPHUB_MODAL = new Modal();
  window.IPHUB_TOOLTIP = new Tooltip();
  window.IPHUB_NOTIFICATION = new Notification();

  // Tippy initialization is now handled by Tippy Manager in app.js

  console.debug("UI Components: Initialized");
  return true;
}
